import React, { useState } from "react";
import { Filter, RotateCcw, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { sportFilterConfig, playerMatchesSport, playerMatchesPosition } from "@/lib/sportFilters";
import { translateFr as tfr } from "@/i18n";

export const LEVEL_GROUPS = [
  { v: "amateur", label: "Amateur", match: ["N3", "départemental", "régional", "amateur"] },
  { v: "semipro", label: "Semi-pro", match: ["N2", "National", "semi"] },
  { v: "pro", label: "Pro", match: ["L1", "L2", "Ligue", "Pro"] },
];
export const RELANCE_OPTIONS = [
  { v: "all", label: tfr("Toutes") },
  { v: "todo", label: tfr("À relancer (> 14 j)") },
  { v: "late", label: tfr("En retard (> 30 j)") },
];

export const DEFAULT_FILTERS = {
  sport: "__all__",
  position: "__all__",
  status: "__all__",
  ageMin: "",
  ageMax: "",
  level: "__all__",
  relance: "all",
};

/**
 * PipelineFilters — collapsible filter card.
 * Sport → Poste/Rôle dynamic pair on top, driven by the shared sports catalogue.
 * Mobile-first stacked grid (2 cols ≥640px, 3 cols ≥1024px). Fully controlled.
 */
export default function PipelineFilters({
  filters,
  onChange,
  onReset,
  resultCount,
  totalCount,
  statuses = [],
  sports = [],
  defaultOpen = true,
  hideHeader = false,
}) {
  const [open, setOpen] = useState(defaultOpen);
  const isOpen = hideHeader ? true : open;

  const positionConfig = sportFilterConfig(sports, filters.sport);
  const positionLabel = positionConfig?.label || tfr("Poste");
  const allPositionsLabel = positionConfig?.all_label || tfr("Tous les postes");

  const update = (key, value) => {
    const next = { ...filters, [key]: value };
    if (key === "sport") next.position = "__all__"; // reset incompatible position
    onChange?.(next);
  };

  const activeCount = countActive(filters);
  const filtered = resultCount !== totalCount;

  return (
    <div
      className="rounded-3xl border border-border bg-card overflow-hidden"
      data-testid="pipeline-filters-card"
    >
      {/* Header */}
      {!hideHeader && (
      <div className="flex items-center justify-between gap-3 px-4 md:px-5 py-3.5">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="flex items-center gap-2 min-w-0 flex-1 text-left group"
          data-testid="pipeline-filters-toggle"
          aria-expanded={open}
        >
          <Filter
            className={cn(
              "h-4 w-4 shrink-0 transition-colors",
              activeCount > 0 ? "text-primary" : "text-muted-foreground"
            )}
          />
          <span className="text-sm font-semibold tracking-tight">Filtres</span>
          {activeCount > 0 && (
            <Badge
              className="bg-primary/15 text-primary border-primary/30 text-[10px] h-5 px-1.5 ml-1 tabular-nums"
              data-testid="pipeline-filters-active-count"
              aria-label={`${activeCount} filtre${activeCount > 1 ? "s" : ""} actif${activeCount > 1 ? "s" : ""}`}
            >
              {activeCount}
            </Badge>
          )}
          {filtered && (
            <span className="text-[11px] text-muted-foreground ml-2 truncate">
              · {resultCount} sur {totalCount}
            </span>
          )}
        </button>
        <div className="flex items-center gap-1">
          {activeCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onReset}
              className="h-8 gap-1.5 text-xs text-muted-foreground hover:text-foreground hover:bg-secondary/60"
              data-testid="pipeline-filters-reset"
            >
              <RotateCcw className="h-3.5 w-3.5" /> Réinitialiser
            </Button>
          )}
          <button
            onClick={() => setOpen((o) => !o)}
            className="rounded-md p-1.5 text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors"
            aria-label={open ? tfr("Réduire les filtres") : tfr("Afficher les filtres")}
          >
            {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
        </div>
      </div>
      )}

      {/* Body — collapsible */}
      <div
        className={cn(
          "grid transition-[grid-template-rows] duration-200 ease-out",
          isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        )}
      >
        <div className="overflow-hidden">
          <div className={cn("px-4 md:px-5 pb-4 pt-1", !hideHeader && "border-t border-border/60")}>
            {hideHeader && (
              <div className="flex items-center justify-between pt-3">
                <span className="text-xs text-muted-foreground" data-testid="pipeline-filters-active-count">
                  {activeCount > 0 ? `${activeCount} ${activeCount > 1 ? tfr("filtres actifs") : tfr("filtre actif")} · ${resultCount} ${tfr("sur")} ${totalCount}` : tfr("Affinez votre pipeline")}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onReset}
                  disabled={activeCount === 0}
                  className="h-8 gap-1.5 text-xs text-muted-foreground hover:text-foreground"
                  data-testid="pipeline-filters-reset"
                >
                  <RotateCcw className="h-3.5 w-3.5" /> Réinitialiser
                </Button>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-3">
              {/* Sport */}
              <FilterField label="Sport" htmlFor="filter-sport">
                <Select
                  value={filters.sport}
                  onValueChange={(v) => update("sport", v)}
                >
                  <SelectTrigger
                    id="filter-sport"
                    className="h-11 border-border bg-background/60"
                    data-testid="filter-sport"
                  >
                    <SelectValue placeholder={tfr("Tous les sports")} />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="__all__">{tfr("Tous les sports")}</SelectItem>
                    {sports.map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FilterField>

              {/* Poste / Rôle — dynamic based on sport */}
              <FilterField label={positionLabel} htmlFor="filter-position">
                <Select
                  value={filters.position}
                  onValueChange={(v) => update("position", v)}
                  disabled={!positionConfig}
                >
                  <SelectTrigger
                    id="filter-position"
                    className="h-11 border-border bg-background/60 disabled:opacity-55"
                    data-testid="filter-position"
                    aria-describedby="filter-position-helper"
                    aria-disabled={!positionConfig}
                  >
                    <SelectValue placeholder={allPositionsLabel} />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="__all__">{allPositionsLabel}</SelectItem>
                    {(positionConfig?.options || []).map((o) => (
                      <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p
                  id="filter-position-helper"
                  className="text-xs text-muted-foreground/70 leading-relaxed"
                  data-testid="filter-position-helper"
                >
                  {tfr("Postes selon le sport sélectionné")}
                </p>
              </FilterField>

              {/* Statut */}
              <FilterField label={tfr("Statut")} htmlFor="filter-status">
                <Select
                  value={filters.status}
                  onValueChange={(v) => update("status", v)}
                >
                  <SelectTrigger
                    id="filter-status"
                    className="h-11 border-border bg-background/60"
                    data-testid="filter-status"
                  >
                    <SelectValue placeholder={tfr("Tous les statuts")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">{tfr("Tous les statuts")}</SelectItem>
                    {statuses.map((s) => (
                      <SelectItem key={s} value={s}>{tfr(s)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FilterField>

              {/* Âge — Min / Max */}
              <FilterField label={tfr("Âge")}>
                <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2">
                  <Input
                    type="number"
                    inputMode="numeric"
                    min={14}
                    max={50}
                    placeholder="Min"
                    aria-label={tfr("Âge minimum")}
                    value={filters.ageMin}
                    onChange={(e) => update("ageMin", e.target.value)}
                    className="h-11 border-border bg-background/60 text-sm tabular-nums"
                    data-testid="filter-age-min"
                  />
                  <span className="text-muted-foreground/60 text-xs shrink-0">—</span>
                  <Input
                    type="number"
                    inputMode="numeric"
                    min={14}
                    max={50}
                    placeholder="Max"
                    aria-label={tfr("Âge maximum")}
                    value={filters.ageMax}
                    onChange={(e) => update("ageMax", e.target.value)}
                    className="h-11 border-border bg-background/60 text-sm tabular-nums"
                    data-testid="filter-age-max"
                  />
                </div>
              </FilterField>

              {/* Niveau */}
              <FilterField label="Niveau actuel" htmlFor="filter-level">
                <Select
                  value={filters.level}
                  onValueChange={(v) => update("level", v)}
                >
                  <SelectTrigger
                    id="filter-level"
                    className="h-11 border-border bg-background/60"
                    data-testid="filter-level"
                  >
                    <SelectValue placeholder={tfr("Tous les niveaux")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">{tfr("Tous les niveaux")}</SelectItem>
                    {LEVEL_GROUPS.map((l) => (
                      <SelectItem key={l.v} value={l.v}>{l.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FilterField>

              {/* Relance */}
              <FilterField label={tfr("Relance")} htmlFor="filter-relance">
                <Select
                  value={filters.relance}
                  onValueChange={(v) => update("relance", v)}
                >
                  <SelectTrigger
                    id="filter-relance"
                    className="h-11 border-border bg-background/60"
                    data-testid="filter-relance"
                  >
                    <SelectValue placeholder={tfr("Toutes")} />
                  </SelectTrigger>
                  <SelectContent>
                    {RELANCE_OPTIONS.map((r) => (
                      <SelectItem key={r.v} value={r.v}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FilterField>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function FilterField({ label, htmlFor, children, className }) {
  return (
    <div className={cn("space-y-1.5", className)}>
      <label
        htmlFor={htmlFor}
        className="text-[11px] uppercase tracking-wider text-muted-foreground/80 font-medium"
      >
        {label}
      </label>
      {children}
    </div>
  );
}

export function countActive(f) {
  let n = 0;
  if (f.sport && f.sport !== "__all__") n++;
  if (f.position && f.position !== "__all__") n++;
  if (f.status && f.status !== "__all__") n++;
  if (f.ageMin || f.ageMax) n++;
  if (f.level && f.level !== "__all__") n++;
  if (f.relance && f.relance !== "all") n++;
  return n;
}

/**
 * applyPipelineFilters — pure helper to filter a player list against the filters state.
 * `sports` is the shared catalogue (provides sport-dependent position configs).
 */
export function applyPipelineFilters(players, filters, sports = []) {
  if (!filters) return players;
  let arr = players;

  if (filters.sport && filters.sport !== "__all__") {
    arr = arr.filter((p) => playerMatchesSport(p, filters.sport));
    if (filters.position && filters.position !== "__all__") {
      const config = sportFilterConfig(sports, filters.sport);
      arr = arr.filter((p) => playerMatchesPosition(p, config, filters.position));
    }
  }

  if (filters.status && filters.status !== "__all__") {
    arr = arr.filter((p) => p.status === filters.status);
  }

  const min = parseInt(filters.ageMin, 10);
  const max = parseInt(filters.ageMax, 10);
  if (!Number.isNaN(min)) arr = arr.filter((p) => (p.age || 0) >= min);
  if (!Number.isNaN(max)) arr = arr.filter((p) => (p.age || 999) <= max);

  if (filters.level && filters.level !== "__all__") {
    const grp = LEVEL_GROUPS.find((g) => g.v === filters.level);
    if (grp) {
      arr = arr.filter((p) => {
        const lvl = (p.level || "").toLowerCase();
        return grp.match.some((m) => lvl.includes(m.toLowerCase()));
      });
    }
  }

  if (filters.relance && filters.relance !== "all") {
    arr = arr.filter((p) => {
      if (!p.last_interaction_at) return true; // no contact = always "à relancer"
      const days = Math.floor((Date.now() - new Date(p.last_interaction_at).getTime()) / 86400000);
      if (filters.relance === "todo") return days > 14;
      if (filters.relance === "late") return days > 30;
      return true;
    });
  }

  return arr;
}
